import { defineStore } from "pinia"

import { mapSyncDraftMetaToDashboardCounts } from "@/modules/sync/syncDraftMeta"
import syncService from "@/services/syncService"
import { SyncDraftMeta, Synchronization } from "@/types/sync.type"

export type createSyncPayload = {
  condition_key: Synchronization["condition_key"]
  condition_value: Synchronization["condition_value"]
  delivery: Synchronization["delivery"]
  discount_percent: Synchronization["discount_percent"]
  format: Synchronization["format"]
  frequency: Synchronization["frequency"]
  load_only: Synchronization["load_only"]
  url: Synchronization["url"]
  warranty: Synchronization["warranty"]
}
type SyncState = {
  synchronization: Synchronization | null
  synchronizationsDraftsMetaFetching: boolean
  synchronizationsDraftsMeta: SyncDraftMeta
  fetching: boolean
  creatingRequest: boolean
  updatingRequest: boolean
  deletingRequest: boolean
}

const initialSyncDraftsMeta: SyncDraftMeta = {
  offersAllCount: 0,
  offersIsSellingCount: 0,
  offersNotSellingCount: 0,
  offersWithErrorsCount: 0,
  offersIsNeedActionCount: 0
}

export const useSyncStore = defineStore("sync", {
  state: (): SyncState => ({
    synchronization: null,
    synchronizationsDraftsMetaFetching: false,
    synchronizationsDraftsMeta: initialSyncDraftsMeta,
    fetching: false,
    creatingRequest: false,
    updatingRequest: false,
    deletingRequest: false
  }),

  getters: {},

  actions: {
    async getSynchronization(uuid: string) {
      this.fetching = true
      try {
        const response = await syncService.getSynchronization(uuid)

        // Large parser reports are replaced on reload; avoid observing every array item in Vue 2.
        if (Array.isArray(response.data.errors?.parse_errors)) {
          Object.freeze(response.data.errors.parse_errors)
        }

        this.synchronization = response.data
        return response.data
      } catch (e: any) {
        throw e?.response?.data || {}
      } finally {
        this.fetching = false
      }
    },

    async getSyncDraftsMeta(uuid: string) {
      // Reset meta data before fetching
      this.synchronizationsDraftsMeta = initialSyncDraftsMeta
      this.synchronizationsDraftsMetaFetching = true

      try {
        const { data: sellingData } = await syncService.getOffersIsSellingInSync(uuid)
        const offersIsSellingCount = sellingData.count

        this.synchronizationsDraftsMeta = {
          ...this.synchronizationsDraftsMeta,
          offersIsSellingCount
        }

        const { data: draftsData } = await syncService.getDraftsMeta(uuid)

        this.synchronizationsDraftsMeta = {
          ...this.synchronizationsDraftsMeta,
          ...mapSyncDraftMetaToDashboardCounts(offersIsSellingCount, draftsData)
        }
      } catch (e) {
        console.error(`Error loading meta for sync ${uuid}:`, e)
      }

      this.synchronizationsDraftsMetaFetching = false
    },

    async createSynchronization(payload: createSyncPayload) {
      this.creatingRequest = true
      try {
        const response = await syncService.createSynchronization(payload)
        return response.data
      } catch (e: any) {
        throw e?.response?.data || {}
      } finally {
        this.creatingRequest = false
      }
    },

    async updateSynchronization({ data, uuid }: { data: any; uuid: string }) {
      this.updatingRequest = true
      try {
        const response = await syncService.updateSynchronization({
          data,
          uuid
        })
        return response.data
      } catch (e: any) {
        throw e?.response?.data || {}
      } finally {
        this.updatingRequest = false
      }
    },

    async deleteSynchronization(uuid: string) {
      try {
        const response = await syncService.deleteSynchronization(uuid)

        return response.data
      } catch (e: any) {
        throw e?.response?.data || {}
      }
    }
  }
})
